import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { requirePermission } from "../../../../../shared/routing/requirePermission";
import {
  ASSET_TYPES_CREATE,
  ASSET_TYPES_UPDATE,
  ASSET_TYPES_VIEW,
} from "../../../assetsPermissions";
import {
  archivedAssetTypesSearchSchema,
  assetTypesSearchSchema,
} from "./searchSchemas";
import type { AssetTypesRouteFactory } from "./assetTypesRouteContext";

const AssetTypesListScreen = lazyRouteComponent(
  () => import("../AssetTypesListScreen"),
  "AssetTypesListScreen",
);
const AssetTypeFormScreen = lazyRouteComponent(
  () => import("../AssetTypeFormScreen"),
  "AssetTypeFormScreen",
);
const AssetTypeDetailScreen = lazyRouteComponent(
  () => import("../AssetTypeDetailScreen"),
  "AssetTypeDetailScreen",
);
const ArchivedAssetTypesListScreen = lazyRouteComponent(
  () => import("../ArchivedAssetTypesListScreen"),
  "ArchivedAssetTypesListScreen",
);

export const createAssetTypesRoutes: AssetTypesRouteFactory = ({
  context,
  assetTypes,
  companies,
}) => {
  const listBasePath = `${context.basePath}/types`;

  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${listBasePath}/$id/edit`,
    beforeLoad: requirePermission([ASSET_TYPES_UPDATE]),
    component: function AssetTypeEditRouteScreen() {
      const { id } = editRoute.useParams() as unknown as { id: string };
      return createElement(AssetTypeFormScreen, {
        repository: assetTypes,
        basePath: listBasePath,
        companies,
        assetTypeId: id,
      });
    },
  });

  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${listBasePath}/$id`,
    beforeLoad: requirePermission([ASSET_TYPES_VIEW]),
    component: function AssetTypeDetailRouteScreen() {
      const { id } = detailRoute.useParams() as unknown as { id: string };
      return createElement(AssetTypeDetailScreen, {
        repository: assetTypes,
        basePath: listBasePath,
        assetTypeId: id,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: listBasePath,
      validateSearch: assetTypesSearchSchema,
      beforeLoad: requirePermission([ASSET_TYPES_VIEW]),
      component: () =>
        createElement(AssetTypesListScreen, {
          repository: assetTypes,
          basePath: listBasePath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${listBasePath}/create`,
      beforeLoad: requirePermission([ASSET_TYPES_CREATE]),
      component: () =>
        createElement(AssetTypeFormScreen, {
          repository: assetTypes,
          basePath: listBasePath,
          companies,
        }),
    }),
    // 'create' and 'archived' are literal segments, registered before the
    // '$id' routes so neither is ever captured as an id.
    editRoute,
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${listBasePath}/archived`,
      validateSearch: archivedAssetTypesSearchSchema,
      beforeLoad: requirePermission([ASSET_TYPES_VIEW]),
      component: () =>
        createElement(ArchivedAssetTypesListScreen, {
          repository: assetTypes,
          basePath: listBasePath,
        }),
    }),
    // Bare '$id' is the most general path here -- kept last.
    detailRoute,
  ];
};
